'use client';

import { ChangeEvent, FormEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { INewsMeta } from '@/models/INewsMeta';
import { twMerge } from 'tailwind-merge';
import MenuArrows from '../components/svg/MenuArrows';
import DatePickerComp from '../components/DatePicker';
import Close from '../components/svg/Close';
import ArticleBrief from '../components/ArticleBrief';
import { useDebounce } from '../../hooks/useDebounce';
import { apiService } from '../../services/ApiService';
import { clearQueryParams, deleteQueryParams, getQueryParam, setQueryParam } from '../../utils/querystring';
import { getQueryFormattedDate } from '../../utils/date';
import { useRenderCount } from '../../hooks/useFirstRender';
import { ICommonCategoryMeta, IPost, IPostFilter } from '../../models/ICategory';
import Link from 'next/link';
import { isBrowser } from '../../utils/isBrowser';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { IPaginatedData } from '../../models/ICommon';
import Button, { ButtonTheme } from '../components/Button';
import DropdownMenu from '../components/DropdownMenu';
import Search from '../components/svg/Search';
import SearchText from '../components/svg/SearchText';
import { MenuSelect } from '../components/MenuSelect';


interface Props {
  initialData: {
    posts: IPaginatedData<IPost>;
  };
  initalFilters?: Partial<IPostFilter>;
  categories: ICommonCategoryMeta[];
  writers: {uniqueCode: string; name: string; isUser: boolean;}[];
  locales: {
    filters: string;
    clearFilters: string;
    writer: string;
    title: string;
    total: string;
    search: string;
    category: string;
    date: {
      from :string;
      to: string;
    };
    loading: string;
    button: string;
  };
  locale: string;
}
export default function Filters({ initialData, locales, categories, writers, initalFilters, locale }: Props) {
  const inputRef = useRef<any>();

  const initialState = {
    dirty: initalFilters ? Object.keys(initalFilters).filter(x => x !== 'q').some(x => initalFilters[x]) : false,
    raw: initialData.posts,
    total: initialData.posts.data.length,
    displayedPosts: initialData.posts.data || [],
    isLoading: false,
    search: initalFilters?.q || '',
    filters: {
      q: initalFilters?.q || '',
      category: initalFilters?.category || '',
      fromDate: initalFilters?.fromDate as Date,
      toDate: initalFilters?.toDate as Date,
      writer: initalFilters?.writer || ''
    }
  }

  const [state, setState] = useState<
    {
      dirty: boolean,
      raw: IPaginatedData<IPost>;
      displayedPosts: IPost[];
      isLoading: boolean;
      search: string;
      filters: {
        q: string;
        category?: string;
        writer?: string;
        fromDate: Date | undefined;
        toDate: Date | undefined;
      }
    }
  >(initialState);

  const debouncedKeyword = useDebounce<string>(state.filters.q);

  const queryClient = useQueryClient();


  const queryKey = [locale, 
    // ...(state.filters ? Object.entries(state.filters || {}).map((key,val) => `${key}=${val}`) : []),
     'search'];

  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage
  } = useInfiniteQuery({
    queryKey,
    retry: false,
    enabled: false,
    initialData: () => {
      return {
        pageParams: [undefined, 1],
        pages: [initialData.posts]
      }
    },
    queryFn: async ({ pageParam = 1 }) => {
      const result = await apiService.searchPosts({ ...state.filters!, q: debouncedKeyword }, { page: pageParam as number, pageSize: initialData.posts.pageSize });
      return result;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return 1;
      return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1
  });

  const totalData = useMemo(
    () => (data?.pages ? data.pages.map(item => item.data).flat(Infinity) : []),
    [data]
  ) as IPost[];


  useEffect(() => {
    const q = getQueryParam('q');
    const category = getQueryParam('category');
    const writer = getQueryParam('writer');
    const fromDate = getQueryParam('fromDate');
    const toDate = getQueryParam('toDate');

    const keys = ['q', 'category', 'fromDate', 'toDate', 'writer'];

    const params = keys.map(k => getQueryParam(k)).filter(x => x);
    if (!params.length) return;
      setState(prevState => ({
        ...prevState,
        filters: {
          ...prevState.filters,
          ...(q ? {q} : {}),
          ...(writer ? {writer} : {}),
          ...(category ? {category} : {}),
          ...(fromDate ? {fromDate: getQueryFormattedDate(fromDate)} : {}) as any,
          ...(toDate ? {toDate: getQueryFormattedDate(toDate)} : {}) as any,
        }
      }));


      return () => {
        queryClient.invalidateQueries({queryKey});
      }
    
  },[]);


  const handleClearFilters = () => {
    setState({
      dirty: false,
      displayedPosts: [],
      search: '',
      filters: {
        fromDate: undefined,
        toDate: undefined,
        q: '',
        category: '',
        writer: ''
      },
      isLoading: false,
      raw: {
        ...state.raw,
        data: [],
        totalPages: 0,
        totalRecords: 0,
        page: 1,
        pageSize: 10
      }
    });

    queryClient.setQueryData(queryKey, () => ({
      pages: [{ page: 1, pageSize: 10, totalPages: 0, totalRecords: 0, data: [] }],
      pageParams: [undefined, 1],
    }));

    const params = Object.keys(state.filters);
    deleteQueryParams(params);
  }



  const handleFilterChange = async (key: keyof typeof state['filters'], value: any) => {
    setState(prevState => ({ ...prevState, dirty: true, filters: { ...prevState.filters, [key]: value }, isLoading: true }));
    setQueryParam({ [key]: value });
    const result = await apiService.searchPosts({ ...state.filters, [key]: value });
    queryClient.setQueryData(queryKey, (oldData: IPaginatedData<IPost>) => {
      return {
        pages: [{ page: 1, pageSize: result.pageSize, totalPages: result.totalPages, totalRecords: result.totalRecords, data: result.data }],
        pageParams: [undefined, 1],
      }
    })
    setState(prevState => ({ ...prevState, displayedPosts: result.data, raw: result, isLoading: false }));

  }

  const handleDate = async (field: 'fromDate' | 'toDate', date: Date) => {
    setQueryParam({ [field]: getQueryFormattedDate(date)});


    setState(prevState => ({
      ...prevState,
      dirty: true,
      isLoading: true,
      filters: {
        ...prevState.filters,
        [field]: date
      }
    }));

    const result = await apiService.searchPosts({ ...state.filters, [field]: date });
    setState(prevState => ({ ...prevState, displayedPosts: result.data, isLoading: false, raw: result }));
    queryClient.setQueryData(queryKey, (oldData: IPaginatedData<IPost>) => {
      return {
        pages: [{ page: 1, pageSize: result.pageSize, totalPages: result.totalPages, totalRecords: result.totalRecords, data: result.data }],
        pageParams: [undefined, 1],
      }
    })

  }

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!state.search) return;
    
    setState(prevState => ({...prevState, isLoading: true, filters: {...prevState.filters, q: state.search}}));
    const result = await apiService.searchPosts({...state.filters, q: state.search});
    queryClient.setQueryData(queryKey, (oldData: IPaginatedData<IPost>) => {
      return {
        pages: [{ page: 1, pageSize: result.pageSize, totalPages: result.totalPages, totalRecords: result.totalRecords, data: result.data }],
        pageParams: [undefined, 1],
      }
    });

    setState(prevState => ({...prevState, isLoading: false, displayedPosts: result.data, raw: result}));
  }




  const SubFilters = ({className}: {className?: string}) => {
    return (
<div className={twMerge('flex flex-col desktop:[&>*]:flex-1 desktop:justify-start gap-[12px] border-[1px] border-secondary-400 rounded-[16px] py-[16px] px-[16px] desktop:px-[24px] h-fit flex-[5] mx-[12px] desktop:mx-[0px] my-[12px] desktop:my-0', className)}>

<h2 className='mb-[16px]'>{locales.filters}</h2>
<div className='flex items-center desktop:flex-col desktop:items-start gap-[12px]'>

<MenuSelect
  onChange={(value: string) => handleFilterChange('writer', value)}
  options={writers.map(x => ({ title: x.name, value: x.uniqueCode }))}
  selected={state.filters.writer}
  placeholder={locales.writer}
  className={{ wrapper: 'select max-w-[50%] tablet:max-w-none' }}
  />
<MenuSelect
  onChange={(value: string) => handleFilterChange('category', value)}
  options={categories.map(x => ({ title: x.title, value: x.uniqueCode }))}
  selected={state.filters.category}
  placeholder={locales.category}
  className={{ wrapper: 'select max-w-[50%] tablet:max-w-none' }}
  />
  </div>

  <div className='flex flex-col gap-y-[12px]'>
<DatePickerComp placeholder={locales.date.from} field='fromDate' state={{ fromDate: state.filters.fromDate, toDate: state.filters.toDate }} handleDate={handleDate} />
  </div>
  <div className='flex flex-col gap-y-[12px]'>
<DatePickerComp placeholder={locales.date.to} field='toDate' state={{ fromDate: state.filters.fromDate, toDate: state.filters.toDate }} handleDate={handleDate} />
  </div>


<div 
onClick={() => state.dirty ? handleClearFilters() : undefined} 
className={twMerge('sm text-secondary-300 dark:text-white hover:underline cursor-pointer mt-[36px] self-end w-fit select-none', !state.dirty && 'opacity-[0.5] hover:no-underline hover:cursor-default')}
>{locales.clearFilters}</div>
</div>
    )
  }

  return (
    <div className='flex flex-col'>
      <h2 className='hidden'>{locales.title}</h2>
      <div className='flex flex-col gap-[20px]'>


        <form onSubmit={handleSearch} className='flex items-center w-full rounded-[8px] h-[52px]'>
          <div className='w-full tablet:font-light [&>*]:items-center relative flex items-center justify-between bg-white dark:bg-night-300 pr-[12px] rounded-br-[8px] h-full'>

            <div className='flex items-center gap-x-[12px] w-full rounded-[10px] h-full'>
        
                <div className='flex items-center gap-x-[16px] w-full h-full'>
                  <Close 
                  onClick={() => {
                    setState(prevState => ({...prevState, search: '', filters: {...prevState.filters, q: ''}}));
                    deleteQueryParams('q');
                  }} 
                  className='grid place-items-center cursor-pointer w-[36px] h-[36px] aspect-square'
                  />
            <input
              suppressContentEditableWarning
              ref={inputRef}
              autoComplete='off'
              className='outline-none border-none bg-transparent w-full h-full text-night-200 dark:text-white flex-1 md'
              placeholder={locales.search}
              value={state.search}
              name='q'
              onChange={({ target: { value } }) => {
                setState(prevState => ({...prevState, search: value}));
                setQueryParam({q: value});
              }}
              type='text'
            />
          </div>
          {/* <SearchCount className='hidden tablet:flex' /> */}
          <input disabled={state.isLoading || isLoading || isFetching} type="submit" className='hidden z-[-100]' tabIndex={-1} />
</div>
</div>

<button type='submit' disabled={state.isLoading || isLoading || isFetching} className='bg-secondary-400 cursor-pointer grid place-items-center w-[52px] rounded-bl-[8px] h-full'>

              <SearchText className='w-[20px] h-[20px]'/>
</button>
        </form>

        <div className='flex flex-col-reverse desktop:flex-row gap-[20px] px-0 tablet:px-[12px] desktop:px-0'>
            <PostsContiner 
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage} 
            initialData={initialData} 
            locale={locale} 
            locales={locales} 
            isFetching={isFetching} 
            isLoading={isLoading} 
            state={state}
            totalData={totalData}
            />
            <SubFilters/>

        </div>
      </div>

    
    </div>
  )
}


interface PostsContainerProps {
state: any;
totalData: IPost[];
initialData: {posts: IPaginatedData<IPost>};
className?: string;
locale: string;
locales: any;
isLoading: boolean;
isFetching: boolean;
hasNextPage: boolean;
fetchNextPage: Function;
}


  function PostsContiner({className,initialData,state,totalData, locale, hasNextPage,isFetching,isLoading,locales, fetchNextPage}: PostsContainerProps) {
    return (
      <div className='flex flex-col flex-[15] mx-[12px] tablet:mx-[0px]'>

      <div className={twMerge('search-posts', className)}>
      {
        state.isLoading ? null  :
        totalData.length ? totalData.map((p, i) => {
          return (
            <ArticleBrief
            forceUpdate={i <= initialData.posts.pageSize}
            locale={locale}
            hideMeta
            className={{
              wrapper: 'gap-x-[12px] tablet:gap-x-0'
            }}
            data={{
              post: { ...p, imageProps: { priority: i < 5, loading: i < 5 ? 'eager' : 'lazy' } },
              category: { title: p.category.title, slug: p.category.slug, uniqueCode: p.category.uniqueCode }
            }}
            key={i}
            />
            )
          }
          ) : <div className='flex w-full string-18 my-[24px] tablet:my-[42px] desktop:my-[92px] px-[24px]'><span className='whitespace-nowrap w-full' dir='rtl'>المعذرة لا يوجد نتائج</span></div>}
    </div>
    {state.isLoading || isLoading || isFetching ?
        <div className='w-full grid place-items-center string-18 my-[92px]'>{locales.loading}...</div> :
        (
          hasNextPage ?
          <div className='w-full desktop:w-[520px] mt-[60px] h-[60px] px-[24px] flex justify-center items-center mx-auto'>
            <Button locale={locale} title={locales.button} theme={ButtonTheme.primary} showIcon className='h-full' onClick={() => fetchNextPage()} />
              </div> :
            null
        )
      }
          </div>
    )
  }